import { RowAction } from '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table.types';
import { AppType } from '@treasury/core/config';
import {
    TableClickPayload,
    TmTableAction,
    TmTableColsDef,
    TmTableView,
} from '@treasury/presentation/components/jha-wc/tm-table';
import {
    deriveCountLabel,
    mapToTmTableRowActions,
    renderNoContent,
} from '@treasury/presentation/utils/table';
import { format } from 'date-fns';
import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { CompanyUserViewModel } from './company-user.vm';

const SEARCH_PLACEHOLDER = 'Search by name, user ID, or digital ID';

const renderTag = (value: string, enabled: boolean) => {
    return html`<jh-tag
        style=${styleMap({
            '--jh-tag-color-background-enabled': enabled ? '#ecf7e7' : '#eeeeee',
            '--jh-tag-color-text-enabled': enabled ? '#366429' : '#5F6468',
        })}
        .label=${value}
    ></jh-tag>`;
};

function getTableActions(
    handleSettingsClick: () => void,
    handleAddUser: () => void,
    app?: AppType
): TmTableAction<CompanyUserViewModel>[] {
    const actions = [
        {
            label: 'Add User',
            execute: () => handleAddUser(),
            icon: 'plus',
        },
    ];
    if (app === AppType.BackOffice) {
        actions.unshift({
            label: '',
            execute: () => handleSettingsClick(),
            icon: 'gear',
        });
    }
    return actions;
}

export const columns: TmTableColsDef<CompanyUserViewModel> = {
    canEdit: false,
    definitions: [
        { prop: 'userName', label: 'Name' },
        { prop: 'userId', label: 'User ID' },
        { prop: 'digitalId', label: 'Digital ID' },
        { prop: 'roleList', label: 'Role' },
        {
            prop: 'lastViewedForSort',
            label: 'Last Viewed',
            sortable: true,
            render: (value, item) => {
                // Display — for items without a lastViewed date
                // Check the original lastViewed property, not lastViewedForSort (which has epoch for sorting)
                if (!item.lastViewed) {
                    return html`—`;
                }
                // Format the date using the same format as jha-advanced-table's date-time dataType
                // Format: "M/dd/yyyy 'at' h:mm aa" (e.g., "1/15/2024 at 10:30 AM")
                try {
                    const date = new Date(item.lastViewed);
                    if (isNaN(date.valueOf())) {
                        return html`—`;
                    }
                    return html`${format(date, "M/dd/yyyy 'at' h:mm aa")}`;
                } catch {
                    return html`—`;
                }
            },
        },
        {
            prop: 'enrolledStatus',
            label: 'Enrollment',
            render: (value, item) => renderTag(value?.toString() ?? '', item.isEnrolledValue),
        },
        {
            /**
             * We have to use the isActiveValue property here because the proxy is stripping the
             * getter values when the data is rendered in the table.
             */
            prop: 'isActiveValue',
            label: 'Profile',
            render: (value, item) => {
                return renderTag(item.isActiveValue, item.isActiveValue === 'Active');
            },
        },
    ],
};

export function createAllCompanyUsersView(
    rowActions: RowAction[],
    handleRowAction: (event: CustomEvent<{ item: CompanyUserViewModel; action: string }>) => void,
    refreshData: () => Promise<CompanyUserViewModel[]>,
    handleSettingsClick: () => void,
    handleAddUser: () => void,
    handleRowClick: (data: TableClickPayload<CompanyUserViewModel>) => void,
    app: AppType
): TmTableView<CompanyUserViewModel> {
    return {
        label: deriveCountLabel('All Users'),
        onClick: (data: TableClickPayload<CompanyUserViewModel>) => {
            handleRowClick(data);
        },
        reduceData: (event, incoming, current) => {
            const safeCurrent = current ?? [];
            const safeIncoming = incoming ?? [];
            const reduced = [...safeCurrent, ...safeIncoming];
            return reduced;
        },
        tableActions: getTableActions(handleSettingsClick, handleAddUser, app),
        refreshData,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        allowMultiSelect: false,
        filters: [
            {
                prop: 'roleList',
                type: 'single-selection',
            },
            {
                prop: 'enrolledStatus',
                type: 'single-selection',
            },
            {
                prop: 'isActiveValue',
                type: 'single-selection',
            },
        ],
        columns,
        rowActions: mapToTmTableRowActions(rowActions, handleRowAction),
        noContentMessage: searchQuery => {
            const message =
                searchQuery === ''
                    ? 'Add New Company Users'
                    : '(0) Company Users match your search';
            return renderNoContent(message);
        },
    };
}

export function createEnrolledCompanyUsersView(
    rowActions: RowAction[],
    handleRowAction: (event: CustomEvent<{ item: CompanyUserViewModel; action: string }>) => void,
    refreshData: () => Promise<CompanyUserViewModel[]>,
    handleSettingsClick: () => void,
    handleAddUser: () => void,
    handleRowClick: (data: TableClickPayload<CompanyUserViewModel>) => void
): TmTableView<CompanyUserViewModel> {
    return {
        label: deriveCountLabel('Enrolled'),
        onClick: (data: TableClickPayload<CompanyUserViewModel>) => {
            handleRowClick(data);
        },
        reduceData: (event, incoming, current) => {
            const safeCurrent = current ?? [];
            const safeIncoming = incoming ?? [];
            const reduced = [...safeCurrent, ...safeIncoming];
            return reduced.filter(user => user.isEnrolled);
        },
        tableActions: getTableActions(handleSettingsClick, handleAddUser),
        refreshData,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        allowMultiSelect: false,
        filters: [],
        columns,
        rowActions: mapToTmTableRowActions(rowActions, handleRowAction),
        noContentMessage: searchQuery => {
            const message =
                searchQuery === ''
                    ? 'Add New Company Users'
                    : '(0) Company Users match your search';
            return renderNoContent(message);
        },
    };
}

export function createPendingCompanyUsersView(
    rowActions: RowAction[],
    handleRowAction: (event: CustomEvent<{ item: CompanyUserViewModel; action: string }>) => void,
    refreshData: () => Promise<CompanyUserViewModel[]>,
    handleSettingsClick: () => void,
    handleAddUser: () => void,
    handleRowClick: (data: TableClickPayload<CompanyUserViewModel>) => void
): TmTableView<CompanyUserViewModel> {
    return {
        label: deriveCountLabel('Pending'),
        reduceData: (event, incoming, current) => {
            const safeCurrent = current ?? [];
            const safeIncoming = incoming ?? [];
            const reduced = [...safeCurrent, ...safeIncoming];
            return reduced.filter(user => user.isNotEnrolled);
        },
        refreshData,
        tableActions: getTableActions(handleSettingsClick, handleAddUser),
        searchPlaceholder: SEARCH_PLACEHOLDER,
        allowMultiSelect: false,
        onClick: (data: TableClickPayload<CompanyUserViewModel>) => {
            handleRowClick(data);
        },
        filters: [],
        columns,
        rowActions: mapToTmTableRowActions(rowActions, handleRowAction),
        noContentMessage: searchQuery => {
            const message =
                searchQuery === ''
                    ? 'Add New Company Users'
                    : '(0) Company Users match your search';
            return renderNoContent(message);
        },
    };
}
